/**
 * Vajra — the chart adapter.
 *
 * Turns bars into the marks you validate by eye. The RULE itself lives in
 * `vajraEngine.ts`, which imports nothing and has a byte-identical backend twin,
 * so whatever this draws is what a scan would fire on if one is ever wired.
 *
 * WHAT IT DRAWS
 *   ▲ V    entry — the BW long with the higher timeframe extending too
 *   ■ X    exit, coloured by WHY it closed: level (blue), stop (amber),
 *          bars-ran-out (grey). Those are different events and a single colour
 *          would hide which one happened.
 *
 * AUTO-TRADE IS NOT WIRED. There is no scan service, no order path and no alert
 * behind this. The engine is shaped so that wiring one later is a `cp` and a
 * registration rather than a rewrite — but nothing outside the chart calls it.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import {
  ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, ChartMarker,
} from './types';
import { computeBbrSeries, atrSeries } from '../dailyBlastIndicators';
import { computeWaveSeries } from '../signalIndicators';
import { dropFormingHtfBars, transformCandles } from '../candleTransforms';
import { computeVajraTrades, computeRunLengths, VAJRA_DEFAULTS, VajraOptions } from './vajraEngine';

// Colours chosen to read on BOTH themes, because a chart strategy adapter is
// pure and cannot see the theme. The obvious light-theme palette was tried and
// the dark screenshot showed why it does not work: #1565c0 sinks into #0D1117 -
// the same colour the BBR x Wave exit marks had to be lifted off three days ago.
// Rather than thread the theme through ChartStrategyOptions for three hex
// values, these are picked light enough for dark and saturated enough for white.
const MARK_ENTRY = '#00c853';   // green - legible on both, verified in shots
const EXIT_LEVEL = '#42a5f5';   // was #1565c0: invisible on the dark ground
const EXIT_STOP = '#ff9800';    // was #ef6c00: muddy on dark
const EXIT_TIME = '#90a4ae';    // was #607d8b: too close to the dark grid

export function computeVajraOverlay(
  candles: CandleRow[],
  opts?: ChartStrategyOptions,
): StrategyOverlayResult {
  if (!candles || candles.length < 40) return { candleColors: [], markers: [] };

  const cfg: VajraOptions = {
    bbOs: Number.isFinite(Number(opts?.vajraBbOs)) ? Number(opts?.vajraBbOs) : VAJRA_DEFAULTS.bbOs,
    wvOs: Number.isFinite(Number(opts?.vajraWvOs)) ? Number(opts?.vajraWvOs) : VAJRA_DEFAULTS.wvOs,
    requireTurn: opts?.vajraRequireTurn !== false,
    minHtfRun: Number.isFinite(Number(opts?.vajraMinHtfRun)) ? Number(opts?.vajraMinHtfRun) : VAJRA_DEFAULTS.minHtfRun,
    exitLevel: Number.isFinite(Number(opts?.vajraExitLevel)) ? Number(opts?.vajraExitLevel) : VAJRA_DEFAULTS.exitLevel,
    stopAtr: Number.isFinite(Number(opts?.vajraStopAtr)) ? Number(opts?.vajraStopAtr) : VAJRA_DEFAULTS.stopAtr,
    maxHold: Number.isFinite(Number(opts?.vajraMaxHold)) ? Number(opts?.vajraMaxHold) : VAJRA_DEFAULTS.maxHold,
    riskPct: Number.isFinite(Number(opts?.vajraRiskPct)) ? Number(opts?.vajraRiskPct) : VAJRA_DEFAULTS.riskPct,
  };

  // Indicators are computed on RAW market bars, never on the display candle
  // type. Switching the chart to Heikin-Ashi to read the trend must not move
  // where this strategy fires — the same reason the BBR/Wave oscillators grew
  // their own source setting.
  const bars = candles;
  const bbr = computeBbrSeries(bars, 20, 2);
  const wave = computeWaveSeries(bars, 10, 21);
  const atr = atrSeries(bars, 14);

  // ── the higher-timeframe gate ────────────────────────────────────────────
  // The mapped HTF for THIS pane's interval — the app's own ladder, so a 4H
  // chart is gated by the daily and a daily chart by the weekly.
  //
  // CAUSALITY IS THE WHOLE POINT HERE. Each bar may only see the last
  // higher-timeframe bar that had already CLOSED. The obvious alternative —
  // the higher-timeframe bar CONTAINING this one, which is what the context
  // band draws — measured +1.48% against +0.10% and was pure hindsight: that
  // bar closes in this bar's future. Made causal it is worth nothing. So the
  // forming bar is dropped AND the walk stops one bar short.
  const htfRaw = opts?.mappedHtfCandles || [];
  const htfMin = Number(opts?.mappedHtfMinutes) || 0;
  const htfClosed = htfMin > 0 ? dropFormingHtfBars(htfRaw, htfMin) : [];
  const htfRun: Array<number | null> = new Array(bars.length).fill(null);
  if (htfClosed.length > 2) {
    const htfClh = transformCandles(htfClosed, 'CLH');
    const runs = computeRunLengths(htfClh as any);
    const hSec = htfMin * 60;
    let k = -1;
    for (let i = 0; i < bars.length; i++) {
      const t = bars[i].time as number;
      // advance only past higher-timeframe bars that have FULLY closed
      while (k + 1 < htfClh.length && (htfClh[k + 1].time as number) + hSec <= t) k++;
      if (k >= 0) htfRun[i] = runs[k];
    }
  }

  if (cfg.minHtfRun > 0 && htfClosed.length <= 2) {
    return {
      candleColors: [], markers: [],
      notice: 'Vajra needs a higher timeframe and this chart has none above it. '
        + 'The measured edge IS the higher-timeframe gate, so nothing is marked rather '
        + 'than marking the unfiltered signal and calling it Vajra.',
    };
  }

  const trades = computeVajraTrades(
    bars as any, bbr, wave.wt1, atr, htfRun, wave.warmup, bars.length - 1, cfg,
  );

  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();

  for (const t of trades) {
    markers.push({
      time: t.entryTime, position: 'belowBar', text: 'V',
      color: MARK_ENTRY, shape: 'arrowUp', size: 2,
    });
    signalsByTime.set(t.entryTime, { signal: 'BUY', strength: 'HIGH', pos: 'LONG', reason: 'V_HTF' });
    if (t.exitTime != null) {
      markers.push({
        time: t.exitTime, position: 'aboveBar', text: 'X',
        color: t.why === 'stop' ? EXIT_STOP : t.why === 'time' ? EXIT_TIME : EXIT_LEVEL,
        shape: 'circle', size: 2,
      });
      signalsByTime.set(t.exitTime, {
        signal: 'EXIT_LONG', strength: null, pos: 'FLAT',
        reason: t.why === 'stop' ? 'V_STOP' : t.why === 'time' ? 'V_TIME' : 'V_LEVEL',
      });
    }
    // Running position, so hovering any held bar says LONG rather than nothing.
    const last = t.exitIdx == null ? bars.length - 1 : t.exitIdx;
    for (let i = t.entryIdx; i <= last && i < bars.length; i++) {
      positionByTime.set(bars[i].time as number, i === last && t.exitIdx != null ? 'FLAT' : 'LONG');
    }
  }

  const notice = trades.length === 0
    ? 'Vajra has not fired on this range. It is meant to be rare — about 3.2 signals '
      + 'per symbol per year on 4H bars. Scan more symbols rather than loosening it.'
    : undefined;

  return {
    candleColors: [], markers, signalsByTime, positionByTime,
    ...(notice ? { notice } : {}),
  };
}

export const vajraStrategy: ChartStrategy = {
  id: 'vajra',
  name: 'Vajra',
  computeOverlays: computeVajraOverlay,
};
